import { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

/**
 * Input — Anthropic Claude Peach token-aware text field.
 * R4 polish: bg surface, border-strong, focus-border brand-primary + ring brand-primary/30,
 *            error variant danger.
 *
 * Variantlar:
 *   - size: sm (h-8) | md (h-10) | lg (h-11)
 *   - invalid prop — error border (--danger) + ring (--danger)/30
 *   - icon (left/right) — leadingIcon, trailingIcon
 *
 * Token'lar: --form-field-* (tokens.css) — coral focus ring, warm dark bg.
 *
 * A11y:
 *   - aria-invalid avtomatik (invalid bo'lsa)
 *   - aria-describedby — helper/error matn ID
 */
const inputVariants = cva(
  cn(
    "w-full rounded-lg border text-[var(--text-primary)] placeholder:text-[var(--form-field-placeholder)]",
    "bg-[var(--form-field-bg)] border-[var(--form-field-border)]",
    "transition-colors duration-150",
    "hover:border-[var(--form-field-border-hover)]",
    "focus:border-[var(--form-field-border-focus)] focus:bg-[var(--form-field-bg-focus)]",
    "focus:outline-none focus:ring-2 focus:ring-[var(--form-field-ring-focus)]",
    "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-[var(--form-field-bg-disabled)]",
    "read-only:bg-[var(--form-field-bg-disabled)] read-only:text-[var(--text-secondary)]",
  ),
  {
    variants: {
      size: {
        sm: "h-8 px-2.5 text-xs",
        md: "h-10 px-3 text-sm",
        lg: "h-11 px-3.5 text-sm",
      },
      invalid: {
        true: cn(
          "border-[var(--form-error-border)]",
          "focus:border-[var(--form-error-border-focus)] focus:ring-[var(--form-error-ring)]",
        ),
        false: "",
      },
    },
    defaultVariants: { size: "md", invalid: false },
  },
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {
  invalid?: boolean;
  /** Chap tomondagi ikon (Lucide React node) */
  leadingIcon?: React.ReactNode;
  /** O'ng tomondagi ikon yoki tugma */
  trailingIcon?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    { className, size, invalid, leadingIcon, trailingIcon, "aria-invalid": ariaInvalid, ...props },
    ref,
  ) => {
    const hasLeading = Boolean(leadingIcon);
    const hasTrailing = Boolean(trailingIcon);

    if (!hasLeading && !hasTrailing) {
      return (
        <input
          ref={ref}
          aria-invalid={ariaInvalid ?? (invalid ? true : undefined)}
          className={cn(inputVariants({ size, invalid }), className)}
          {...props}
        />
      );
    }

    // Icon mode: relative wrapper
    return (
      <div className="relative w-full">
        {hasLeading && (
          <span
            aria-hidden
            className={cn(
              "pointer-events-none absolute left-0 top-0 flex h-full items-center justify-center text-[var(--text-muted)]",
              size === "sm" ? "w-7" : size === "lg" ? "w-9" : "w-8",
            )}
          >
            {leadingIcon}
          </span>
        )}
        <input
          ref={ref}
          aria-invalid={ariaInvalid ?? (invalid ? true : undefined)}
          className={cn(
            inputVariants({ size, invalid }),
            hasLeading && (size === "sm" ? "pl-7" : size === "lg" ? "pl-9" : "pl-8"),
            hasTrailing && (size === "sm" ? "pr-7" : size === "lg" ? "pr-9" : "pr-8"),
            className,
          )}
          {...props}
        />
        {hasTrailing && (
          <span
            className={cn(
              "absolute right-0 top-0 flex h-full items-center justify-center text-[var(--text-muted)]",
              size === "sm" ? "w-7" : size === "lg" ? "w-9" : "w-8",
            )}
          >
            {trailingIcon}
          </span>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";
